import { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();

  const checkSubscriptionStatus = async (userId) => {
    try {
      const subscriptionsRef = collection(db, 'customers', userId, 'subscriptions');
      const subscriptionsQuery = query(subscriptionsRef, where('status', 'in', ['trialing', 'active']));
      const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
      const hasActiveSubscription = !subscriptionsSnapshot.empty;

      // Update the user's isPremium status in Firestore if it doesn't match subscription status
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists() && userDoc.data().isPremium !== hasActiveSubscription) {
        await updateDoc(doc(db, 'users', userId), {
          isPremium: hasActiveSubscription
        });
      }

      return hasActiveSubscription;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Get user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const hasActiveSubscription = await checkSubscriptionStatus(user.uid);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUser({ 
              ...user, 
              ...userData,
              hasActiveSubscription,
              isPremium: hasActiveSubscription // Ensure isPremium matches subscription status
            });
            setIsPremium(hasActiveSubscription);
          } else {
            // Create new user document if it doesn't exist
            const newUser = {
              email: user.email,
              createdAt: new Date().toISOString(),
              isPremium: hasActiveSubscription
            };
            await setDoc(doc(db, 'users', user.uid), newUser);
            setUser({ 
              ...user, 
              ...newUser,
              hasActiveSubscription
            });
            setIsPremium(hasActiveSubscription);
          }
        } catch (error) {
          console.error('Error setting up user:', error);
          setUser(null);
          setIsPremium(false);
        }
      } else {
        setUser(null);
        setIsPremium(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  // Add a function to manually check and update subscription status
  const refreshSubscriptionStatus = async () => {
    if (user) {
      const hasActiveSubscription = await checkSubscriptionStatus(user.uid);
      setIsPremium(hasActiveSubscription);
      setUser(prev => ({
        ...prev,
        isPremium: hasActiveSubscription,
        hasActiveSubscription
      }));
    }
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      createdAt: new Date().toISOString(),
      isPremium: false
    });

    return userCredential;
  };

  const logout = () => {
    return signOut(auth);
  };

  const value = {
    user,
    isPremium,
    login,
    signup,
    logout,
    refreshSubscriptionStatus // Export the refresh function
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 